import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Bot,
  Loader2,
  Send,
  User,
  Paperclip,
  Scissors,
  X,
  FileText,
  Eye,
  Image,
  ArrowLeft,
} from "lucide-react";
import clsx from "clsx";
import { MoncoLogoLink } from "components/common/MoncoLogoLink";
import api from "../../../../../utils/api";
import { StreamingMarkdown } from "./StreamingMarkdown";
import { ANALYTICS_EVENTS, trackEvent } from "../../../../../utils/analytics";
import { CropModal } from "../ProjectResearchChat/CropModal";
import { PageViewerPanel } from "../PageViewerPanel";
import type {
  ChatMessage,
  ChatAttachment,
  ResearchChatPageProps,
} from "./types";

export const ResearchChatPage = ({
  projectId,
  files,
}: ResearchChatPageProps) => {
  const { t } = useTranslation();

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [streamingIdx, setStreamingIdx] = useState<number | null>(null);
  const [cropTarget, setCropTarget] = useState<{
    filename: string;
    page: number;
    maxPage: number;
  } | null>(null);

  // Page viewer state
  const [viewerFile, setViewerFile] = useState<string | null>(null);
  const [viewerPage, setViewerPage] = useState(1);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const filePickerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, isLoading, scrollToBottom]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [projectId]);

  // Close file picker on outside click
  useEffect(() => {
    if (!showFilePicker) return;
    const handleClick = (e: MouseEvent) => {
      if (
        filePickerRef.current &&
        !filePickerRef.current.contains(e.target as Node)
      ) {
        setShowFilePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showFilePicker]);

  const addAttachment = useCallback((filename: string, pages: number[]) => {
    setAttachments((prev) => {
      const existing = prev.find(
        (a) => a.filename === filename && !a.cropImage,
      );
      if (existing) {
        const merged = new Set([...existing.pages, ...pages]);
        return prev.map((a) =>
          a === existing
            ? { ...a, pages: [...merged].sort((a, b) => a - b) }
            : a,
        );
      }
      return [...prev, { filename, pages }];
    });
    setShowFilePicker(false);
    trackEvent(ANALYTICS_EVENTS.RESEARCH_CHAT_FILE_ATTACHED, {
      filename,
      pageCount: pages.length,
    });
  }, []);

  const removeAttachment = useCallback((idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleCropCapture = useCallback(
    (base64: string) => {
      if (!cropTarget) return;
      setAttachments((prev) => [
        ...prev,
        {
          filename: cropTarget.filename,
          pages: [cropTarget.page],
          cropImage: base64,
          cropLabel: `${cropTarget.filename} p.${cropTarget.page} (${t("smartSurveyor.researchReport.crop", "crop")})`,
        },
      ]);
      trackEvent(ANALYTICS_EVENTS.RESEARCH_CHAT_CROP_ATTACHED, {
        filename: cropTarget.filename,
        page: cropTarget.page,
      });
      setCropTarget(null);
    },
    [cropTarget, t],
  );

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const attachedPages: Array<{ filename: string; pages: number[] }> = [];
    const images: Array<{
      data: string;
      mime_type: string;
      label: string;
    }> = [];

    for (const att of attachments) {
      if (att.cropImage) {
        images.push({
          data: att.cropImage,
          mime_type: "image/png",
          label: att.cropLabel || `${att.filename} crop`,
        });
      } else {
        attachedPages.push({ filename: att.filename, pages: att.pages });
      }
    }

    const userMsg: ChatMessage = {
      role: "user",
      text,
      attachments: attachments.length > 0 ? [...attachments] : undefined,
    };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setAttachments([]);
    setIsLoading(true);

    trackEvent(ANALYTICS_EVENTS.RESEARCH_CHAT_MESSAGE_SENT, {
      attachmentCount: attachedPages.length,
      cropCount: images.length,
      conversationLength: messages.length,
    });

    try {
      const conversationHistory = messages.map((m) => ({
        role: m.role,
        content: m.text,
      }));

      const response = await (api as Record<string, Function>).researchChat(
        text,
        projectId,
        conversationHistory.length > 0 ? conversationHistory : null,
        attachedPages.length > 0 ? attachedPages : null,
        images.length > 0 ? images : null,
      );

      if (response?.answer) {
        const updated = [
          ...newMessages,
          { role: "assistant" as const, text: response.answer },
        ];
        setMessages(updated);
        setStreamingIdx(updated.length - 1);
        trackEvent(ANALYTICS_EVENTS.RESEARCH_CHAT_RESPONSE_RECEIVED);
      } else {
        trackEvent(ANALYTICS_EVENTS.RESEARCH_CHAT_RESPONSE_FAILED, {
          error: "No answer",
        });
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t(
              "smartSurveyor.researchReport.chatError",
              "Failed to get a response.",
            );
      setMessages([
        ...newMessages,
        { role: "assistant", text: `Error: ${errorMessage}` },
      ]);
      trackEvent(ANALYTICS_EVENTS.RESEARCH_CHAT_RESPONSE_FAILED, {
        error: errorMessage,
      });
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [input, isLoading, attachments, messages, projectId, t]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handlePageClick = useCallback((file: string, page: number) => {
    setViewerFile(file);
    setViewerPage(page);
  }, []);

  const openCropFromToolbar = useCallback(() => {
    const defaultFile = attachments[0]?.filename || files[0]?.filename;
    if (!defaultFile) return;
    const fileEntry = files.find((f) => f.filename === defaultFile);
    const pg = attachments[0]?.pages[0] || 1;
    setCropTarget({
      filename: defaultFile,
      page: pg,
      maxPage: fileEntry?.page_count ?? 1,
    });
  }, [attachments, files]);

  // Auto-resize textarea
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInput(e.target.value);
      const el = e.target;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    },
    [],
  );

  const hasMessages = messages.length > 0;

  return (
    <>
      <div className="h-screen w-screen flex bg-gray-50 overflow-hidden">
        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4 shrink-0">
            <MoncoLogoLink className="h-7" />
            <div className="h-5 w-px bg-gray-300" />
            <button
              type="button"
              onClick={() => window.history.back()}
              className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              <ArrowLeft size={16} />
              <span className="font-medium">
                {t("smartSurveyor.common.back")}
              </span>
            </button>
            <div className="h-5 w-px bg-gray-300" />
            <div className="flex items-center gap-2">
              <Bot size={18} className="text-blue-600" />
              <span className="text-sm font-semibold text-gray-800">
                {t("smartSurveyor.researchReport.projectChat", "Project Chat")}
              </span>
            </div>
          </div>

          {/* Messages area */}
          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto min-h-0"
          >
            {!hasMessages ? (
              /* Empty state */
              <div className="h-full flex items-center justify-center">
                <div className="text-center max-w-md px-6">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-blue-100 flex items-center justify-center">
                    <Bot size={32} className="text-blue-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    {t(
                      "smartSurveyor.researchReport.projectChat",
                      "Project Chat",
                    )}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {t(
                      "smartSurveyor.researchReport.chatPlaceholder",
                      "Ask questions about the project. You can attach specific pages for context.",
                    )}
                  </p>
                </div>
              </div>
            ) : (
              /* Messages list */
              <div className="max-w-3xl mx-auto w-full px-4 py-6 space-y-6">
                {messages.map((msg, i) => (
                  <div key={i} className="flex items-start gap-3">
                    {/* Avatar */}
                    <div
                      className={clsx(
                        "shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5",
                        msg.role === "assistant"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 text-gray-600",
                      )}
                    >
                      {msg.role === "assistant" ? (
                        <Bot size={16} />
                      ) : (
                        <User size={16} />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-500 mb-1">
                        {msg.role === "assistant"
                          ? t(
                              "smartSurveyor.researchReport.assistantLabel",
                              "Assistant",
                            )
                          : t("smartSurveyor.researchReport.youLabel", "You")}
                      </div>

                      {/* Attached pages / crops */}
                      {msg.role === "user" &&
                        msg.attachments &&
                        msg.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {msg.attachments.map((att, j) => (
                              <button
                                key={j}
                                type="button"
                                onClick={() =>
                                  handlePageClick(att.filename, att.pages[0])
                                }
                                className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full border border-blue-200 hover:bg-blue-100 transition-colors"
                              >
                                {att.cropImage ? (
                                  <Image size={12} />
                                ) : (
                                  <FileText size={12} />
                                )}
                                <span>
                                  {att.cropLabel ||
                                    `${att.filename} p.${att.pages.join(",")}`}
                                </span>
                                <Eye size={12} />
                              </button>
                            ))}
                          </div>
                        )}

                      {msg.role === "assistant" ? (
                        <StreamingMarkdown
                          text={msg.text}
                          animate={streamingIdx === i}
                          onComplete={() => setStreamingIdx(null)}
                          onTick={
                            streamingIdx === i ? scrollToBottom : undefined
                          }
                        />
                      ) : (
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">
                          {msg.text}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white mt-0.5">
                      <Bot size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-gray-500 mb-1">
                        {t(
                          "smartSurveyor.researchReport.assistantLabel",
                          "Assistant",
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Loader2
                          size={16}
                          className="animate-spin text-blue-500"
                        />
                        <span className="text-sm text-gray-500">
                          {t(
                            "smartSurveyor.researchReport.chatThinking",
                            "Thinking...",
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="border-t border-gray-200 bg-white shrink-0">
            {/* Attachment chips */}
            {attachments.length > 0 && (
              <div className="max-w-3xl mx-auto w-full px-4 pt-3 flex flex-wrap gap-1.5">
                {attachments.map((att, i) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full border border-blue-200"
                  >
                    {att.cropImage ? (
                      <img
                        src={`data:image/png;base64,${att.cropImage}`}
                        alt="crop"
                        className="w-5 h-5 rounded object-cover"
                      />
                    ) : (
                      <FileText size={12} />
                    )}
                    <span>
                      {att.cropLabel ||
                        `${att.filename} p.${att.pages.join(",")}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="hover:text-red-500 transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="max-w-3xl mx-auto w-full px-4 py-4">
              <div className="relative flex items-end gap-2 bg-gray-50 border border-gray-300 rounded-2xl px-4 py-3 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                {/* Attach button */}
                <button
                  type="button"
                  onClick={() => setShowFilePicker((prev) => !prev)}
                  disabled={files.length === 0}
                  className={clsx(
                    "p-1.5 rounded-lg transition-colors shrink-0 mb-0.5",
                    showFilePicker
                      ? "bg-blue-100 text-blue-600"
                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-200",
                    files.length === 0 && "opacity-30 cursor-not-allowed",
                  )}
                  title={t(
                    "smartSurveyor.researchReport.attachPages",
                    "Attach pages",
                  )}
                >
                  <Paperclip size={18} />
                </button>

                {/* Crop button */}
                <button
                  type="button"
                  onClick={openCropFromToolbar}
                  disabled={files.length === 0}
                  className={clsx(
                    "p-1.5 rounded-lg transition-colors shrink-0 mb-0.5",
                    "text-gray-400 hover:text-gray-600 hover:bg-gray-200",
                    files.length === 0 && "opacity-30 cursor-not-allowed",
                  )}
                  title={t(
                    "smartSurveyor.researchReport.cropFromPage",
                    "Crop from page",
                  )}
                >
                  <Scissors size={18} />
                </button>

                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={t(
                    "smartSurveyor.researchReport.chatInputPlaceholder",
                    "Ask about the project...",
                  )}
                  rows={1}
                  disabled={isLoading}
                  className="flex-1 resize-none bg-transparent text-sm focus:outline-none disabled:opacity-50 max-h-[200px] py-1"
                />

                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className={clsx(
                    "p-2 rounded-xl text-white transition-colors shrink-0 mb-0.5",
                    !input.trim() || isLoading
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700",
                  )}
                >
                  <Send size={16} />
                </button>

                {/* File picker dropdown */}
                {showFilePicker && (
                  <FilePickerDropdown
                    ref={filePickerRef}
                    files={files}
                    onSelect={addAttachment}
                    onView={(filename, page) => {
                      handlePageClick(filename, page);
                      setShowFilePicker(false);
                    }}
                    onCrop={(filename, page, maxPage) => {
                      setCropTarget({ filename, page, maxPage });
                      setShowFilePicker(false);
                    }}
                    onClose={() => setShowFilePicker(false)}
                  />
                )}
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-2">
                {t(
                  "smartSurveyor.researchReport.chatDisclaimer",
                  "AI can make mistakes. Verify important information.",
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Page viewer side panel */}
        {viewerFile && (
          <PageViewerPanel
            projectId={projectId}
            file={viewerFile}
            page={viewerPage}
            maxPage={
              files.find((f) => f.filename === viewerFile)?.page_count ?? 0
            }
            onPageChange={setViewerPage}
            onClose={() => setViewerFile(null)}
          />
        )}
      </div>

      {/* Crop modal */}
      {cropTarget && (
        <CropModal
          projectId={projectId}
          filename={cropTarget.filename}
          page={cropTarget.page}
          maxPage={cropTarget.maxPage}
          onCapture={handleCropCapture}
          onClose={() => setCropTarget(null)}
          onPageChange={(page) =>
            setCropTarget((prev) => (prev ? { ...prev, page } : null))
          }
        />
      )}
    </>
  );
};

// ── File Picker Dropdown ──────────────────────────────────────

interface FilePickerDropdownProps {
  files: Array<{ filename: string; page_count: number }>;
  onSelect: (filename: string, pages: number[]) => void;
  onView: (filename: string, page: number) => void;
  onCrop: (filename: string, page: number, maxPage: number) => void;
  onClose: () => void;
}

const FilePickerDropdown = React.forwardRef<
  HTMLDivElement,
  FilePickerDropdownProps
>(({ files, onSelect, onView, onCrop, onClose }, ref) => {
  const { t } = useTranslation();
  const [expandedFile, setExpandedFile] = useState<string | null>(null);
  const [pageInput, setPageInput] = useState("");

  const parsePages = useCallback((input: string, maxPage: number): number[] => {
    const pages = new Set<number>();
    for (const part of input.split(",")) {
      const trimmed = part.trim();
      const range = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
      if (range) {
        const start = Math.max(1, parseInt(range[1]));
        const end = Math.min(maxPage, parseInt(range[2]));
        for (let i = start; i <= end; i++) pages.add(i);
      } else {
        const n = parseInt(trimmed);
        if (n >= 1 && n <= maxPage) pages.add(n);
      }
    }
    return [...pages].sort((a, b) => a - b);
  }, []);

  return (
    <div
      ref={ref}
      className="absolute bottom-full left-0 mb-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-20 max-h-72 flex flex-col"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 shrink-0">
        <span className="text-xs font-semibold text-gray-600">
          {t(
            "smartSurveyor.researchReport.attachProjectFiles",
            "Attach project files",
          )}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <X size={14} />
        </button>
      </div>
      <div className="overflow-y-auto flex-1">
        {files.map((f) => (
          <div
            key={f.filename}
            className="border-b border-gray-50 last:border-0"
          >
            <button
              type="button"
              onClick={() => {
                setExpandedFile(
                  expandedFile === f.filename ? null : f.filename,
                );
                setPageInput("");
              }}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={14} className="text-gray-400 shrink-0" />
                <span className="text-xs text-gray-700 truncate">
                  {f.filename}
                </span>
              </div>
              <span className="text-[10px] text-gray-400 shrink-0 ml-2">
                {f.page_count}{" "}
                {t("smartSurveyor.researchReport.pages", "pages")}
              </span>
            </button>

            {expandedFile === f.filename && (
              <div className="px-3 pb-2 space-y-2">
                <input
                  type="text"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const pages = parsePages(pageInput, f.page_count);
                      if (pages.length > 0) {
                        onSelect(f.filename, pages);
                        setPageInput("");
                      }
                    }
                  }}
                  placeholder={t(
                    "smartSurveyor.researchReport.pagesInputPlaceholder",
                    "Pages (e.g. 1,3,5-8) — max {{max}}",
                    { max: f.page_count },
                  )}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      const pages = pageInput.trim()
                        ? parsePages(pageInput, f.page_count)
                        : Array.from({ length: f.page_count }, (_, i) => i + 1);
                      if (pages.length > 0) {
                        onSelect(f.filename, pages);
                        setPageInput("");
                      }
                    }}
                    className="text-[10px] font-medium bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                  >
                    {pageInput.trim()
                      ? t(
                          "smartSurveyor.researchReport.attachPages",
                          "Attach pages",
                        )
                      : t(
                          "smartSurveyor.researchReport.attachAll",
                          "Attach all",
                        )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const pg = pageInput.trim()
                        ? parseInt(pageInput) || 1
                        : 1;
                      onCrop(f.filename, pg, f.page_count);
                    }}
                    className="text-[10px] font-medium text-orange-600 border border-orange-300 px-2 py-1 rounded hover:bg-orange-50 transition-colors flex items-center gap-1"
                  >
                    <Scissors size={10} />
                    {t(
                      "smartSurveyor.researchReport.cropFromPage",
                      "Crop from page",
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const pg = pageInput.trim()
                        ? parseInt(pageInput) || 1
                        : 1;
                      onView(f.filename, pg);
                    }}
                    className="text-[10px] font-medium text-blue-600 border border-blue-300 px-2 py-1 rounded hover:bg-blue-50 transition-colors flex items-center gap-1"
                  >
                    <Eye size={10} />
                    {t("smartSurveyor.researchReport.viewPage", "View page")}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

FilePickerDropdown.displayName = "FilePickerDropdown";
